import {
  Component,
  input,
  OnDestroy,
  OnInit,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { AuthService, User } from '@services/auth.service';
import { Subscription } from 'rxjs';
import { NavbarButtonComponent } from './NavbarButton/navbar-button.component';
import { Router } from '@angular/router';
import { PromptService } from '@services/prompt.service';
import { GestionLinker } from '@services/gestion.service';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-profile-card',
  standalone: true,
  imports: [CommonModule, NavbarButtonComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div
      (click)="toggleDropdown($event)"
      class="group relative w-full h-full bg-[#494949] rounded flex items-center transition-all cursor-pointer"
      [class.justify-center]="!showName()"
    >
      @if (this.user) {
        <div class="p-1.5 h-full">
          <div
            class="h-full aspect-square rounded-full bg-vert-1 flex items-center justify-center text-white font-bold text-[12px]"
          >
            <span>{{ this.computeInitials(this.user) }}</span>
          </div>
        </div>

        @if (this.showName()) {
          <span class="text-xs font-semibold block flex-1 truncate">{{
            this.user.username
          }}</span>
          <span class="material-icons px-2"> keyboard_arrow_down </span>
        }

        <div
          class="absolute left-0 top-full right-0 bg-[#494949] h-fit
           transition-all duration-150 ease-out z-50"
          [ngClass]="{
            'opacity-0 invisible pointer-events-none !-translate-y-[2px] scale-95':
              !isDropdownOpen,
            'opacity-100 visible pointer-events-auto translate-y-0 scale-100':
              isDropdownOpen,
          }"
          (click)="$event.stopPropagation()"
        >
          <div class="w-full mb-1 flex flex-col gap-1 p-1">
            <app-navbar-button
              (onClick)="openSettings()"
              label="users.preferences"
              svgType="material"
              svgName="settings"
              [isCollapsed]="!showName()"
              style="filter: brightness(0.9)"
            />
            <app-navbar-button
              [url]="'/user/' + this.user.id + '/user-reset'"
              label="users.change-password"
              svgType="material"
              svgName="lock"
              style="filter: brightness(0.9)"
              [isCollapsed]="!showName()"
              (onClick)="closeDropdown()"
            />
            <app-navbar-button
              (onClick)="logout()"
              label="users.logout"
              svgType="material"
              svgName="logout"
              [isCollapsed]="!showName()"
              style="filter: brightness(0.9)"
            />
          </div>
        </div>
      } @else {
        <div
          class="p-1.5 w-full h-full flex items-center justify-center text-center fg-gris-2 text-sm font-bold"
        >
          <span>Chargement...</span>
        </div>
      }
    </div>
  `,
})
export class ProfileCardComponent implements OnInit, OnDestroy {
  sub = new Subscription();

  protected user: User | null = null;
  protected isDropdownOpen = false;
  onPreferencesOpenRequest = output<void>();

  showName = input.required<boolean>();

  constructor(
    private auth: AuthService,
    private router: Router,
    private promptService: PromptService,
    private translate: TranslateService,
    private toast: ToastrService,
    private gestion: GestionLinker
  ) {}

  ngOnInit(): void {
    this.sub.add(
      this.auth.user$.subscribe(user => {
        this.user = user;
      })
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  computeInitials(user: User): string {
    return user.username
      ? user.username.charAt(0).toUpperCase()
      : (user.firstname || '').charAt(0).toUpperCase() +
          (user.lastname || '').charAt(0).toUpperCase();
  }

  toggleDropdown(event: Event): void {
    event.stopPropagation();
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  logout(): void {
    this.isDropdownOpen = false;
    this.auth.logout();
    this.router.navigate(['/']);
  }

  askForMailConfirmation() {
    this.isDropdownOpen = false;
    this.promptService
      .askForConfirmation(
        'users.reset_password_modal_title',
        'users.reset_own_password_modal_message',
        'common.confirm',
        'common.cancel'
      )
      .then(confirmed => {
        if (!confirmed) return;
        if (!this.user) return;

        this.gestion.resetPasswordForMyself().subscribe({
          next: res => {
            if (!res.success) {
              const retryAfter = (res.retry_after as number) || 20;

              this.toast.error(
                this.translate.instant('users.password_reset_error', {
                  retryAfter: retryAfter,
                })
              );
              return;
            } else {
              this.toast.success(
                this.translate.instant('users.password_reset_success')
              );
            }
          },
          error: err => {
            console.error('error()', err);
          },
        });
      });
  }

  protected closeDropdown() {
    this.isDropdownOpen = false;
  }

  openSettings() {
    this.closeDropdown();
    this.onPreferencesOpenRequest.emit();
  }
}
